import { importDb } from "../lib/import-db";
import type { RawHeader, RawRow } from "../lib/import-db";
import type {
  SearchParams,
  SortParams,
  FetchResult,
} from "../components/Manager/types";

/**
 * Check if raw data exists in IndexedDB (i.e. the checkbox was checked during import).
 */
export async function hasRawData(): Promise<boolean> {
  const count = await importDb.rawRows.count();
  return count > 0;
}

/**
 * Fetch all raw headers, ordered by column letter.
 */
export async function fetchRawHeaders(): Promise<RawHeader[]> {
  const headers = await importDb.rawHeaders.toArray();
  // Sort by key length first (A before AA), then alphabetically
  headers.sort((a, b) => {
    if (a.key.length !== b.key.length) return a.key.length - b.key.length;
    return a.key.localeCompare(b.key);
  });
  return headers;
}

/**
 * Apply filters to raw rows and return the matching set.
 * Filters are Record<string, unknown> where each key is a column letter
 * and the value is an array of selected distinct values.
 */
async function getFilteredRows(
  filters?: Record<string, unknown>,
): Promise<RawRow[]> {
  let rows = await importDb.rawRows.toArray();

  if (filters) {
    for (const [colKey, selectedValues] of Object.entries(filters)) {
      if (
        !selectedValues ||
        !Array.isArray(selectedValues) ||
        selectedValues.length === 0
      ) {
        continue;
      }
      const valueSet = new Set(selectedValues.map(String));
      rows = rows.filter((row) => valueSet.has(String(row[colKey] ?? "")));
    }
  }

  return rows;
}

/**
 * Fetch raw data rows with pagination, filtering, and sorting.
 * Used as the `fetchData` prop for Manager.Root.
 */
export async function fetchRawData(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  sortParams?: SortParams,
): Promise<FetchResult<RawRow>> {
  const rows = await getFilteredRows(searchParams?.filters);

  const totalCount = rows.length;

  // Apply sorting (default: by lineNumber ascending)
  const sortCol = sortParams?.column ?? "lineNumber";
  const sortOrder = sortParams?.order ?? "asc";
  rows.sort((a, b) => {
    const aVal = a[sortCol];
    const bVal = b[sortCol];
    if (aVal === undefined || aVal === null) return 1;
    if (bVal === undefined || bVal === null) return -1;
    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortOrder === "desc" ? bVal - aVal : aVal - bVal;
    }
    const cmp = String(aVal).localeCompare(String(bVal));
    return sortOrder === "desc" ? -cmp : cmp;
  });

  // Apply pagination
  const start = offset ?? 0;
  const end = limit ? start + limit : rows.length;
  const data = rows.slice(start, end);
  const hasMore = end < totalCount;

  return { data, hasMore, totalCount };
}

/**
 * Fetch distinct values for a specific column, applying cross-filters
 * (all other filters except the current one).
 * Used as the `fetchDistinctValues` prop for Manager.DynamicSearch.Root.
 */
export async function fetchDistinctRawValues(
  filterKey: string,
  otherFilters?: Record<string, unknown>,
): Promise<{ value: string | number; label: string }[]> {
  // Apply all filters EXCEPT the one we're fetching distinct values for
  const crossFilters: Record<string, unknown> = {};
  if (otherFilters) {
    for (const [key, val] of Object.entries(otherFilters)) {
      if (key !== filterKey) {
        crossFilters[key] = val;
      }
    }
  }

  const rows = await getFilteredRows(crossFilters);

  // Collect distinct values for the target column
  const distinctSet = new Set<string>();
  for (const row of rows) {
    const val = String(row[filterKey] ?? "");
    if (val !== "") {
      distinctSet.add(val);
    }
  }

  return Array.from(distinctSet)
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }));
}
